import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, RefreshCw, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import { apiFetch } from "@/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
    { id: "enquiries", label: "Enquiries", cols: ["created_at", "name", "classes_name", "mobile", "email", "product", "status"] },
    { id: "demo-requests", label: "Demo Requests", cols: ["created_at", "name", "email", "company", "phone", "date", "time", "product", "status"] },
];

const Admin = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState("enquiries");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch(`/admin/${tab}`);
            setRows(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        if (user?.role === "admin") load();
    }, [user, load]);

    const updateStatus = async (id, status) => {
        try {
            await apiFetch(`/admin/${tab}/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
            setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
            toast.success(`Marked as ${status}.`);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const exportCsv = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${tab}.csv`, { credentials: "include" });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${tab}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded.");
        } catch (err) {
            toast.error(err.message);
        }
    };

    if (!user || user.role !== "admin") {
        return (
            <section className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="admin-denied">
                <ShieldAlert className="mx-auto h-10 w-10 text-ember" />
                <h1 className="mt-4 font-serif text-2xl font-semibold text-ink">Admins only</h1>
                <p className="mt-2 text-sm text-ink/55">Sign in with an admin account to view submissions.</p>
                <Link to="/login" className="mt-6 inline-block rounded-full bg-ember px-7 py-3 text-sm font-semibold text-bone hover:bg-ember-dark">
                    Go to login
                </Link>
            </section>
        );
    }

    const active = TABS.find((t) => t.id === tab);

    return (
        <>
            <Seo title="Admin | Coaching Crafters AI" description="Submissions dashboard." path="/admin" />
            <section className="mx-auto max-w-7xl px-6 py-14 sm:px-10" data-testid="admin-page">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-serif text-3xl font-semibold text-ink">Submissions</h1>
                        <p className="mt-1 text-sm text-ink/55">Enquiries and demo requests from the website.</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={load}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink hover:border-ember hover:text-ember"
                        >
                            <RefreshCw className="h-4 w-4" /> Refresh
                        </button>
                        <button
                            data-testid="admin-export-button"
                            onClick={exportCsv}
                            className="inline-flex items-center gap-2 rounded-full bg-ember px-5 py-2.5 text-sm font-semibold text-bone hover:bg-ember-dark"
                        >
                            <Download className="h-4 w-4" /> Export CSV
                        </button>
                    </div>
                </div>

                <div className="mt-8 flex gap-2">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                                tab === t.id ? "bg-ember text-bone" : "border border-ink/15 text-ink hover:border-ember hover:text-ember"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-6 overflow-x-auto rounded-3xl border border-[#E2E8F4] bg-white shadow-sm">
                    {loading ? (
                        <p className="p-10 text-center text-sm text-ink/45">Loading…</p>
                    ) : rows.length === 0 ? (
                        <p className="p-10 text-center text-sm text-ink/45" data-testid="admin-empty">
                            No {active.label.toLowerCase()} yet.
                        </p>
                    ) : (
                        <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-table">
                            <thead>
                                <tr className="border-b border-[#E2E8F4] font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">
                                    {active.cols.map((c) => (
                                        <th key={c} className="px-4 py-3">{c.replace(/_/g, " ")}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E2E8F4]">
                                {rows.map((r) => (
                                    <tr key={r.id} className="text-ink/75">
                                        {active.cols.map((c) =>
                                            c === "status" ? (
                                                <td key={c} className="px-4 py-3">
                                                    <select
                                                        data-testid={`status-select-${r.id}`}
                                                        value={r.status}
                                                        onChange={(e) => updateStatus(r.id, e.target.value)}
                                                        className="rounded-lg border border-[#E2E8F4] bg-bone px-2 py-1.5 text-xs font-semibold text-ink focus:border-ember"
                                                    >
                                                        <option value="new">new</option>
                                                        <option value="contacted">contacted</option>
                                                        <option value="closed">closed</option>
                                                    </select>
                                                </td>
                                            ) : (
                                                <td key={c} className="max-w-[200px] truncate px-4 py-3">
                                                    {c === "created_at" ? new Date(r[c]).toLocaleString("en-IN") : r[c] || "—"}
                                                </td>
                                            )
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>
        </>
    );
};

export default Admin;
